import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/components/layout/AppShell';
import { Select } from '@/components/ui/Select';
import { ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { ReviewQueue, DecidedList } from '@/components/review/ReviewQueue';
import { useAppStore, useIsStaff } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { fetchReviewWorkspaces, type ReviewWorkspace } from '@/lib/reviewSources';
import { api, type Suggestion } from '@/lib/api';

const ALL = '';

/**
 * The global review inbox. Defaults to EVERY workspace the user can review,
 * merged — the sidebar badge counts across all of them, so the page must show
 * the same universe or the two disagree. The picker narrows to one engagement.
 */
export function ReviewPage() {
  const isStaff = useIsStaff();
  const sessionCompanies = useAppStore((s) => s.companies);
  const workspaces = useAsync(() => fetchReviewWorkspaces(), []);
  const [selectedId, setSelectedId] = useState(ALL);

  const wsList: ReviewWorkspace[] = workspaces.status === 'success' ? workspaces.data : [];
  const wsById = useMemo(() => new Map(wsList.map((w) => [w.id, w])), [wsList]);

  const suggestions = useAsync(async () => {
    if (workspaces.status !== 'success') return [] as Suggestion[];
    const targets = selectedId === ALL ? wsList : wsList.filter((w) => w.id === selectedId);
    // A client without suggestion:view on a workspace gets a 403 → counts as
    // empty, mirroring the badge's semantics.
    const lists = await Promise.all(
      targets.map((w) => api.listSuggestions(w.id).catch(() => [] as Suggestion[])),
    );
    return lists
      .flat()
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [workspaces.status, selectedId]);

  // Refetch when the workspace list first lands (suggestions ran before it).
  useEffect(() => {
    if (workspaces.status === 'success') suggestions.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.status]);

  // Everything visible is decidable: staff everywhere; clients only ever see
  // workspaces where they hold ADMIN (the API 403s the rest).
  const canDecide = isStaff || sessionCompanies.some((c) => c.role === 'ADMIN');

  // Row context label: engagement (and company) the item belongs to.
  const contextOf = useMemo(
    () => (s: Suggestion) => {
      const w = wsById.get(s.workspaceId);
      return w ? `${w.companyName} · ${w.name}` : null;
    },
    [wsById],
  );

  const pending = suggestions.status === 'success' ? suggestions.data.filter((s) => s.status === 'PENDING') : [];
  const decided = suggestions.status === 'success' ? suggestions.data.filter((s) => s.status !== 'PENDING') : [];

  return (
    <>
      <PageHeader
        kicker="Inbox"
        title="Review"
        description="Approve or decline what the agents propose."
        action={
          wsList.length > 1 ? (
            <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="w-auto min-w-56">
              <option value={ALL}>All engagements</option>
              {wsList.map((w) => {
                const n = pending.filter((s) => s.workspaceId === w.id).length;
                return (
                  <option key={w.id} value={w.id}>
                    {w.companyName} · {w.name}{n > 0 ? ` (${n} pending)` : ''}
                  </option>
                );
              })}
            </Select>
          ) : undefined
        }
      />

      {workspaces.status === 'loading' && <SkeletonRows rows={3} />}
      {workspaces.status === 'error' && <ErrorState error={workspaces.error} onRetry={workspaces.reload} />}
      {workspaces.status === 'success' && wsList.length === 0 && (
        <p className="text-sm text-fg-muted">No workspaces to review yet.</p>
      )}

      {workspaces.status === 'success' && wsList.length > 0 && suggestions.status === 'loading' && (
        <SkeletonRows rows={3} />
      )}
      {suggestions.status === 'error' && <ErrorState error={suggestions.error} onRetry={suggestions.reload} />}
      {workspaces.status === 'success' && suggestions.status === 'success' && (
        <>
          <ReviewQueue
            pending={pending}
            canDecide={canDecide}
            projectNameOf={contextOf}
            onDecided={(msg) => {
              if (msg) toast.success(msg);
              suggestions.reload();
            }}
          />
          <DecidedList items={decided} projectNameOf={contextOf} />
        </>
      )}
    </>
  );
}
